import GameScene from "../scenes/GameScene";
import ARIA from "../utils/ARIA";
import Fonts from "../utils/Fonts";
import GameColors from "../utils/GameColors";

export class GameUI {
	scene: GameScene;
	onResize: () => void;
	group: Phaser.GameObjects.Container;

	scoreGroup: Phaser.GameObjects.Container;
	scoreText: Phaser.GameObjects.Text;
	progressBar: Phaser.GameObjects.Image;
	progressMarker: Phaser.GameObjects.Image;
	progressGroup: Phaser.GameObjects.Container;

	scoreEmitter: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
	addImage(key: string): Phaser.GameObjects.Image {
		const img = this.scene.add.image(0, 0, key);
		this.group.add(img);
		return img;
	}

	constructor(scene: GameScene, hud: Phaser.GameObjects.Container, topLayer: Phaser.GameObjects.Container, progressMarkers: number) {
		this.scene = scene;
		this.group = hud;

		const scoreGroup = (this.scoreGroup = scene.add.container(0, 0));
		hud.add(scoreGroup);

		const scoreBG = scene.add.image(0, 0, "scoreBG");
		scoreGroup.add(scoreBG);
		scoreGroup.setSize(scoreBG.width, scoreBG.height);

		const scoreText = (this.scoreText = scene.add.text(0, 0, "0", {
			fontSize: "55px",
			color: GameColors.WHITE,
			fontFamily: Fonts.MontserratBold,
			align: "left",
		}));
		scoreGroup.add(scoreText);
		scoreText.setOrigin(0, 0.5);
		scene.alignIn.LeftCenter(scoreText, scoreBG, -170, 5);

		const pauseButton = scene.add.button(0, 0, "pauseBtn", null, this.scene.pauseGame, this.scene, true);
		hud.add(pauseButton);

		const progressGroup = (this.progressGroup = scene.add.container(0, 0));
		this.group.add(progressGroup);

		const progressBG = scene.add.image(0, 0, "progressBG");
		progressGroup.add(progressBG);
		progressGroup.setSize(progressBG.width, progressBG.height);

		const progressBar = (this.progressBar = scene.add.image(0, 0, "progressBar"));
		progressBar.setOrigin(0, 0.5);
		progressBar.setPosition(-progressBar.width / 2, 0);
		progressGroup.add(progressBar);

		const offset = progressBar.width / progressMarkers;
		for (let i = 0; i < progressMarkers + 1; i++) {
			const img = i == 0 || i == progressMarkers ? "progressCircleEnd" : "progressCircleMid";
			const circle = scene.add.image(progressBar.x + offset * i, 0, img);
			if (i == progressMarkers) circle.flipX = true;
			circle.setOrigin(0.5);
			progressGroup.add(circle);
		}

		const progressMarker = (this.progressMarker = this.addImage("progressMarker"));
		progressGroup.add(progressMarker);

		this.onResize = (): void => {
			const viewport = scene.viewport;
			const ratioContainer = scene.ratioContainer;
			if (scene.alignToViewport) {
				scene.alignIn.TopLeft(scoreGroup, viewport, -45, -50);
				scene.alignIn.TopRight(pauseButton, viewport, -40, -40);
			} else {
				scene.alignIn.TopLeft(scoreGroup, ratioContainer, -45, -50);
				scene.alignIn.TopRight(pauseButton, ratioContainer, -40, -40);
			}
			scene.alignIn.BottomCenter(progressGroup, viewport, 0, 0);
			scene.alignIn.LeftCenter(progressMarker, progressBar, 0, 5);
		};
		this.resize();

		// ARIA Accessability
		ARIA.addTextGroup({
			container: scoreBG,
			textField: scoreText,
			setText: (text: string) => {
				return `Current Score: ${text}`;
			},
		});
		pauseButton.setARIA("Pause");

		this.progressGroup.setVisible(false);
	}

	update(dt: number): void {
		// update any ui game objects as needed
	}

	setScore(score: number): void {
		const str = score.toFixed(0);
		if (this.scoreText.text != str) this.scoreText.text = str;
		this.resize();
	}
	tweenScore(): void {
		const emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];

		this.scene.time.delayedCall(200, () => {
			this.scoreText.setTint(0xff0000);

			for (let i = 0; i < 8; i++) {
				const particles = this.scene.add.particles("pickupStar");
				this.scoreGroup.add(particles);

				const emitter = particles.createEmitter({
					alpha: { start: 1, end: 0.0 },
					scale: { min: 1, max: 1.5 },
					frequency: 600,
					lifespan: Phaser.Math.RND.between(1000, 1500),
					angle: Phaser.Math.RND.between(-170, 170),
					speedX: { min: -100, max: 150 },
					speedY: { min: 100, max: 200 },
				});
				emitter.setPosition(this.scoreText.x - 90, emitter.y);
				this.scoreEmitter.push(emitter);
				emitter.start();
			}
		});

		this.scene.add.tween({
			targets: this.scoreText,
			scale: { from: 1, to: 1.4 },
			duration: 500,
			yoyo: true,
			onComplete: () => {
				this.scoreText.setTint(0xffffff);
				this.scene.time.delayedCall(500, () => {
					for (const emitter of emitters) {
						emitter.stop();
					}
				});
			},
			ease: Phaser.Math.Easing.Back.Out,
		});
	}

	onPause(): void {
		for (const emitter of this.scoreEmitter) {
			emitter.pause();
		}
	}

	onResume(): void {
		for (const emitter of this.scoreEmitter) {
			emitter.resume();
		}
	}

	setProgress(progress: number): void {
		this.progressMarker.x = this.progressBar.x + progress * this.progressBar.width;
	}

	resize(): void {
		this.onResize();
	}
}
